import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/asphalt-shingles-roof.js';



export default function AsphaltShinglesPage() {
  const service = {
    title: 'Asphalt Shingles Roofing',
    image: '/hero/best-asphalt-shingles-roof-services-in-los-angeles.webp',
    description:
      'Protect your home with asphalt shingles roofing by Modern Build. Durable, affordable solutions. Free estimate: (323) 300-4130',
    link: '/services/asphalt-shingles',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

    // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Asphalt Shingles Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Asphalt Shingles Roofing in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="Asphalt Shingles Roofing in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Protect your home with asphalt shingles roofing by Modern Build. Durable, affordable solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="asphalt shingles roofing Los Angeles, roof replacement, shingle roofing contractor, Modern Build roofing"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/asphalt-shingles" />

        <meta property="og:title" content="Asphalt Shingles Roofing in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Protect your home with asphalt shingles roofing by Modern Build. Durable, affordable solutions. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-asphalt-shingles-roof-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/asphalt-shingles"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Asphalt Shingles Roofing in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Protect your home with asphalt shingles roofing by Modern Build. Durable, affordable solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-asphalt-shingles-roof-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateWebPageSchema({
          title: 'Asphalt Shingles Roofing in Los Angeles | Free Estimate | Modern Build',
          description:
            'We install high-quality asphalt shingles roofing across Los Angeles. Weather-resistant, energy-efficient, and backed by warranty.',
          slug: '/services/asphalt-shingles',
          }))
        }} />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="asphaltRoof" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="RELIABLE ROOFING SOLUTIONS"
        title="Top-Quality Asphalt Shingles Roofing in Los Angeles"
        background="white"
        description="Our asphalt shingles roofing combines durability, aesthetics, and affordability. Whether you're replacing a damaged roof or upgrading your home’s exterior, we deliver expert installation that stands the test of time."
        extraText="We offer a wide selection of shingle colors and profiles, all backed by strong warranties and installed with precision by our certified roofing crew."
        phone="(323) 300-4130"
        image1="/split/asphalt-shingles-roof-installation-los-angeles.webp"
        image2="/split/new-shingle-roofing-project-modern-home.webp"
      />

      <Benefits
        title="Why Choose Modern Build for Asphalt Shingles Roofing?"
        subtitle="We bring craftsmanship and care to every roof"
        items={[
          { icon: <ShieldCheck className="w-8 h-8 text-primary" />, title: "Work Performed by Licensed, Insured Roofers" },
          { icon: <CheckCircle className="w-8 h-8 text-primary" />, title: "Premium Shingle Materials" },
          { icon: <Wrench className="w-8 h-8 text-primary" />, title: "Full Roof Replacement & Repair" },
          { icon: <Ruler className="w-8 h-8 text-primary" />, title: "Precise Installation" },
          { icon: <Hammer className="w-8 h-8 text-primary" />, title: "Leak-Free Guarantee" },
          { icon: <DollarSign className="w-8 h-8 text-primary" />, title: "Competitive Pricing" },
          { icon: <ThumbsUp className="w-8 h-8 text-primary" />, title: "Great Curb Appeal" },
          { icon: <Home className="w-8 h-8 text-primary" />, title: "Built to Withstand LA Weather" },
        ]}
      />

      <ContentBlock
        subtitle="LOS ANGELES SHINGLE ROOF SPECIALISTS"
        title="Long-Lasting, Affordable Roofing"
        description="Asphalt shingles are the most popular roofing material for a reason. They’re affordable, versatile, and offer strong weather resistance. Our team helps you choose the best shingle for your home’s style and performance needs."
        extraText="We use proven techniques and attention to detail to ensure your roof is watertight, properly ventilated, and ready for decades of protection."
        buttonText="Request a Roofing Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Asphalt Roofing Process"
        subtitle="Step-by-step roofing done right"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free Roof Assessment",
            description: "We inspect your current roof and recommend the best solution based on condition and budget.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Material Selection",
            description: "Choose from a range of high-quality shingles in various colors and profiles.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Installation Day",
            description: "Our team removes old materials, installs new underlayment, and secures your new roof properly.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Inspection",
            description: "We inspect every detail, clean up thoroughly, and walk you through your new roof features.",
          },
        ]}
      />

      <Split
        subtitle="PROTECTION YOU CAN TRUST"
        title="Upgrade Your Home with Asphalt Shingles"
        background="white"
        description="From traditional homes to modern estates, our asphalt shingles roofing enhances appearance and performance. Every roof is built to last with materials from top brands and a team that stands behind every install."
        extraText="We offer warranties, maintenance plans, and support long after installation is complete."
        phone="(323) 300-4130"
        image1="/split/modern-home-asphalt-shingles-roofing.webp"
        image2="/split/asphalt-roofing-installation-in-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Schedule a Free Roof Inspection"
        subtitle="Shingle roofing you can count on"
        buttonText="Book My Roofing Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="asphalt-shingles-roof" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
